// App.js
import React, { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router } from "react-router-dom";

import { useToast } from "./ui/Toast";
import { useConfirm } from "./ui/ConfirmDialog";

import { onAuthStateChanged } from "firebase/auth";
import { auth, authReady } from "./firebase";

import SignIn from "./SignIn";
import AppRoutes from "./routes/AppRoutes";
function App() {
  const { showToast } = useToast();
  const { confirm } = useConfirm();

  // Sign-in overlay
  const [showSignIn, setShowSignIn] = useState(false);

  // Hydration guards
  const [domHydrated, setDomHydrated] = useState(false);
  useEffect(() => setDomHydrated(true), []);

  const [authHydrated, setAuthHydrated] = useState(false); // resolves when firebase has restored session

  // User + UI state
  const [user, setUser] = useState(null);
  const [userCountry, setUserCountry] = useState("Unknown"); // kept for Settings
  const [loading, setLoading] = useState(true);

  // Settings (kept)
  const [autoSearchNext, setAutoSearchNext] = useState(() => {
    const saved = localStorage.getItem("autoSearchNext");
    return saved ? JSON.parse(saved) : true;
  });
  useEffect(() => {
    localStorage.setItem("autoSearchNext", JSON.stringify(autoSearchNext));
  }, [autoSearchNext]);

  const [menuActive, setMenuActive] = useState(false);

  // Track when Firebase emits auth events. We'll use this to suppress
  // accidental SignIn overlay during the brief cross-tab rehydration window.
  const lastAuthEventAtRef = useRef(0);

  // Wait for Firebase to hydrate *once* across this tab
  useEffect(() => {
    let mounted = true;
    authReady.then(() => {
      if (!mounted) return;
      setAuthHydrated(true);
    });
    return () => { mounted = false; };
  }, []);

  // Subscribe to auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      lastAuthEventAtRef.current = Date.now();
      setUser(currentUser);
      // When both DOM and auth are hydrated, we can stop showing any splash
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  // 💳 NEW: Handle return from successful unban payment
  useEffect(() => {
    // 1. Check if the URL has ?unbanned=true
    const params = new URLSearchParams(window.location.search);
    
    if (params.get("unbanned") === "true") {
      // 2. Show the "Welcome Back" toast
      showToast({
        title: "Welcome Back!",
        description: "Your account has been successfully restored. You can now join the chat.",
        variant: "success",
      });

      // 3. Clean up the URL so the message doesn't show again if they refresh
      const newUrl = window.location.pathname;
      window.history.replaceState({}, document.title, newUrl);
    }
  }, [showToast]); // Only runs on mount

  // Only allow opening SignIn after a small grace period from the last auth event.
  // This avoids other tabs seeing a SignIn overlay if this tab is refreshing.
  const safeOpenSignIn = () => {
    const since = Date.now() - lastAuthEventAtRef.current;
    const graceMs = 1000; // 1s grace is plenty to avoid flicker/race across tabs
    if (!authHydrated) return; // don't open until Firebase is fully ready
    if (since < graceMs) return; // suppress transient null-user blips
    setShowSignIn(true);
  };

  const handleSignIn = (firebaseUser) => {
    setUser(firebaseUser);
    setShowSignIn(false);
  };

  const handleLogout = async () => {
    const ok = await confirm({
      title: user?.isAnonymous ? "End guest session?" : "Sign out?",
      description: user?.isAnonymous
        ? "This will end your guest session. Unsaved preferences may be lost."
        : "You'll be signed out and can sign in again later.",
      confirmText: user?.isAnonymous ? "End session" : "Sign out",
      cancelText: "Cancel",
    });
    if (!ok) return;

    try {
      await auth.signOut();
      showToast({
        title: user?.isAnonymous ? "Guest session ended" : "Signed out",
        description: user?.isAnonymous
          ? "You can sign in to save preferences."
          : "See you next time!",
        variant: "info",
      });
    } catch (error) {
      console.error("Error signing out:", error);
      showToast({
        title: "Logout failed",
        description: "Please try again.",
        variant: "error",
      });
    }
  };

  // REMOVED the blocking if (loading || !authHydrated) statement here!
  // By removing it, the public pages (like Home) will render instantly.

  return (
    <Router>
      <AppRoutes
        // layout
        menuActive={menuActive}
        setMenuActive={setMenuActive}

        // auth
        user={user}
        userCountry={userCountry}
        onLogout={handleLogout}
        
        // Pass the loading state down just in case private routes need it
        isAuthLoading={loading || !authHydrated} 

        // settings
        autoSearchNext={autoSearchNext}
        setAutoSearchNext={setAutoSearchNext}

        // IMPORTANT: guarded openSignIn
        openSignIn={safeOpenSignIn}
      />

      {/* Only show the sign-in modal if DOM is ready, auth is ready, and it's triggered */}
      {domHydrated && authHydrated && showSignIn && (
        <SignIn
          onSignIn={handleSignIn}
          onClose={() => setShowSignIn(false)}
        />
      )}
    </Router>
  );
}

export default App;